import BankAccountForm from './BankAccountForm/BankAccountForm';
import Card from 'components/Card/Card';
import CoverageForm from './CoverageForm/CoverageForm';
import NonResidenceForm from './NonResidenceForm/NonResidenceForm';
import PersonalInfoForm from './PersonalInfoForm/PersonalInfoForm';
import React, { useEffect, useRef, useState } from 'react';
import ResidentForm from './ResidentForm/ResidentForm';
import SunshineZohoFormModal from 'components/SunshineZohoFormModal/SunshineZohoFormModal';
import axios from 'axios';
import {
  sunshineDirectDepositFormUrl,
  sunshineEOCoverageFormUrl,
  sunshinePersonalInfoFormUrl
} from 'helpers/sunshineZohoFormUrls';
import imageCompression from 'browser-image-compression';
import BrandLogo from 'tailwind-components/BrandLogo/BrandLogo';
import propTypes from 'prop-types';
import { cn } from 'lib/utils';
import style from './agentInfo.module.scss';
import { Info, Pencil } from 'lucide-react';
import { getBase64 } from 'helpers/imageProcessing';

const INITIAL_SUNSHINE_MODAL_DATA = {
  title: '',
  iframeTitle: '',
  src: ''
};

function AgentInfo({
  refreshUser,
  setLocalLoader,
  zohoData: user,
  showAlert,
  changeUserProfilePic
}) {
  const [sunshineModalData, setSunshineModalData] = useState(
    INITIAL_SUNSHINE_MODAL_DATA
  );
  const [focusEditBankingInfo, setFocusEditBankingInfo] = useState(false);
  const cancelTokenSource = axios.CancelToken.source();
  const positionAtAgency = user.Position_at_Agency;
  const fileInputRef = useRef(null);
  const editButtonRef = useRef(null);

  useEffect(() => {
    async function refreshUserWrapper() {
      setLocalLoader(true);
      await refreshUser(() => setLocalLoader(false), cancelTokenSource);
    }
    refreshUserWrapper();
    const shouldFocus = !!sessionStorage.getItem('focus_personal_banking_edit');
    setFocusEditBankingInfo(shouldFocus);
    sessionStorage.removeItem('focus_personal_banking_edit');

    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, []);

  useEffect(() => {
    if (focusEditBankingInfo && editButtonRef.current) {
      setLocalLoader(false);
      setTimeout(() => {
        editButtonRef.current.focus();
        editButtonRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 300);
    }
  }, [focusEditBankingInfo]);

  const setProfilePicFile = async e => {
    const options = {
      maxSizeMB: 0.02,
      maxWidthOrHeight: 500,
      useWebWorker: true
    };
    let target = e.target;

    if (!target.files[0].type.includes('image'))
      showAlert({ message: 'File not valid as Profile Pic', code: 'error' });
    else {
      showAlert({ message: 'Updating Profile Photo', code: 'info' });

      const compressedFile = await imageCompression(target.files[0], options);

      let promise = getBase64(compressedFile);
      promise.then(result => {
        changeUserProfilePic(
          result.split(',')[1],
          user.id,
          fileInputRef,
          setLocalLoader,
          cancelTokenSource
        );
      });
    }
  };

  return (
    <>
      <div className={style.agentInfoView}>
        <Card>
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Info
              size={24}
              style={{
                color: '#e3ad32',
                marginRight: 8
              }}
            />
            <span className='font-bold'>
              If you&apos;ve recently updated your information, please allow
              some time for the changes to be reviewed and approved by an
              administrator before they are reflected in the system.
            </span>
          </div>
        </Card>
        <Card>
          <div className={style.title}>Personal Info</div>
          <section className={style.personalInfo}>
            <div className={style.leftImageContainer}>
              <div className={cn(style.imageContainer, 'ring-2 ring-black/30')}>
                <BrandLogo
                  src={user.agentPhoto}
                  className={`${!user.agentPhoto && style.placeholder}`}
                  alt=''
                />
                <label htmlFor='profilePic' className={style.profilePicLabel}>
                  <Pencil />
                  Edit Profile Picture
                </label>
                <input
                  type='file'
                  id='profilePic'
                  className={style.profilePicInput}
                  onChange={setProfilePicFile}
                  ref={fileInputRef}
                />
              </div>
            </div>
            <div>
              <PersonalInfoForm
                user={user}
                onClick={() =>
                  setSunshineModalData({
                    title: 'Personal Information',
                    iframeTitle: 'Individual Personal Information',
                    src: sunshinePersonalInfoFormUrl(user)
                  })
                }
              />
            </div>
          </section>
        </Card>

        <Card>
          <div className={style.title}>Personal Bank Info</div>
          <section>
            <div ref={editButtonRef}>
              <BankAccountForm
                user={user}
                onClick={() =>
                  setSunshineModalData({
                    title: 'Personal Bank Information',
                    iframeTitle: 'Individual Direct Deposit Agreement',
                    src: sunshineDirectDepositFormUrl(user)
                  })
                }
                position={positionAtAgency}
              />
            </div>
          </section>
        </Card>
        <Card>
          <div className={style.title}>E&O Coverage</div>
          <section className={style.coverage}>
            <CoverageForm
              user={user}
              onClick={() =>
                setSunshineModalData({
                  title: 'E&O Coverage',
                  iframeTitle: 'Individual E&O Coverage',
                  src: sunshineEOCoverageFormUrl(user)
                })
              }
            />
          </section>
        </Card>
        <Card>
          <div className={style.title}>Resident State License Information</div>
          <section className={style.resident}>
            <ResidentForm user={user} />
          </section>
        </Card>
        <Card>
          <div className={style.title}>Non-Resident States License Numbers</div>
          <section className={style.nonResidence}>
            <NonResidenceForm user={user} />
          </section>
        </Card>
      </div>
      {!!sunshineModalData.src && (
        <SunshineZohoFormModal
          className={style.modal}
          data={sunshineModalData}
          onClose={() => setSunshineModalData(INITIAL_SUNSHINE_MODAL_DATA)}
          allowCamera={sunshineModalData.title === 'Personal Information'}
        />
      )}
    </>
  );
}

AgentInfo.propTypes = {
  refreshUser: propTypes.func,
  setLocalLoader: propTypes.func,
  showAlert: propTypes.func,
  changeUserProfilePic: propTypes.func,
  zohoData: propTypes.object
};

export default AgentInfo;
